import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  IconButton,
  Chip
} from '@mui/material';
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  ArrowBack as BackIcon,
  TrendingDown as DepreciationIcon,
  Assessment as UtilizationIcon,
  AttachMoney as CostIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const AssetReports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    assetType: '',
    status: ''
  });
  const [reportData, setReportData] = useState({
    depreciation: [],
    utilization: [],
    costAnalysis: []
  });

  useEffect(() => {
    fetchAssets();
  }, [filters]);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/employee-asset/list', {
        assetType: filters.assetType || undefined,
        status: filters.status || undefined,
        page: 1,
        limit: 1000
      });

      if (response.data.success) {
        const assetData = response.data.data;
        setAssets(assetData);
        generateReportData(assetData);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReportData = (assetData) => {
    // Depreciation Report
    const depreciationData = assetData.map(asset => ({
      assetCode: asset.assetCode,
      assetName: asset.assetName,
      purchaseValue: asset.purchaseValue || 0,
      currentValue: asset.currentValue || 0,
      depreciation: (asset.purchaseValue || 0) - (asset.currentValue || 0),
      depreciationRate: asset.depreciationRate || 0,
      age: Math.floor((new Date() - new Date(asset.assignedDate)) / (1000 * 60 * 60 * 24 * 365))
    }));

    // Utilization Report (by type)
    const utilizationByType = {};
    assetData.forEach(asset => {
      if (!utilizationByType[asset.assetType]) {
        utilizationByType[asset.assetType] = {
          total: 0,
          assigned: 0,
          returned: 0,
          damaged: 0,
          lost: 0,
          maintenance: 0
        };
      }
      utilizationByType[asset.assetType].total++;
      utilizationByType[asset.assetType][asset.status.toLowerCase()] = 
        (utilizationByType[asset.assetType][asset.status.toLowerCase()] || 0) + 1;
    });

    const utilizationData = Object.keys(utilizationByType).map(type => ({
      type,
      ...utilizationByType[type],
      utilizationRate: Math.round((utilizationByType[type].assigned / utilizationByType[type].total) * 100)
    }));

    // Cost Analysis
    const costByType = {};
    assetData.forEach(asset => {
      if (!costByType[asset.assetType]) {
        costByType[asset.assetType] = {
          totalPurchaseValue: 0,
          totalCurrentValue: 0,
          totalDepreciation: 0,
          count: 0
        };
      }
      costByType[asset.assetType].totalPurchaseValue += asset.purchaseValue || 0;
      costByType[asset.assetType].totalCurrentValue += asset.currentValue || 0;
      costByType[asset.assetType].totalDepreciation += (asset.purchaseValue || 0) - (asset.currentValue || 0);
      costByType[asset.assetType].count++;
    });

    const costAnalysisData = Object.keys(costByType).map(type => ({
      type,
      ...costByType[type],
      avgPurchaseValue: Math.round(costByType[type].totalPurchaseValue / costByType[type].count),
      avgCurrentValue: Math.round(costByType[type].totalCurrentValue / costByType[type].count)
    }));

    setReportData({
      depreciation: depreciationData,
      utilization: utilizationData,
      costAnalysis: costAnalysisData
    });
  };

  // Export to Excel
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Depreciation Sheet
    const depreciationWS = XLSX.utils.json_to_sheet(reportData.depreciation);
    XLSX.utils.book_append_sheet(wb, depreciationWS, 'Depreciation Report');

    // Utilization Sheet
    const utilizationWS = XLSX.utils.json_to_sheet(reportData.utilization);
    XLSX.utils.book_append_sheet(wb, utilizationWS, 'Utilization Report');

    // Cost Analysis Sheet
    const costWS = XLSX.utils.json_to_sheet(reportData.costAnalysis);
    XLSX.utils.book_append_sheet(wb, costWS, 'Cost Analysis');

    // Asset List Sheet
    const assetListData = assets.map(asset => ({
      'Asset Code': asset.assetCode,
      'Asset Name': asset.assetName,
      'Type': asset.assetType,
      'Status': asset.status,
      'Condition': asset.condition,
      'Employee': asset.employeeId?.staff_name || 'N/A',
      'Employee ID': asset.employeeId?.emp_id || 'N/A',
      'Purchase Value': asset.purchaseValue,
      'Current Value': asset.currentValue,
      'Depreciation': (asset.purchaseValue || 0) - (asset.currentValue || 0),
      'Assigned Date': new Date(asset.assignedDate).toLocaleDateString(),
      'Brand': asset.brand || 'N/A',
      'Model': asset.model || 'N/A',
      'Serial Number': asset.serialNumber || 'N/A'
    }));
    const assetListWS = XLSX.utils.json_to_sheet(assetListData);
    XLSX.utils.book_append_sheet(wb, assetListWS, 'Asset List');

    XLSX.writeFile(wb, `Asset_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text('Asset Management Report', 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 28);

    let yPosition = 40;

    // Summary Section
    doc.setFontSize(14);
    doc.text('Summary', 14, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    const totalDepreciation = reportData.depreciation.reduce((sum, item) => sum + item.depreciation, 0);
    const utilization = Math.round((assets.filter(a => a.status === 'ASSIGNED').length / assets.length) * 100) || 0;
    const currentValue = assets.reduce((sum, asset) => sum + (asset.currentValue || 0), 0);
    
    doc.text(`Total Depreciation: ₹${totalDepreciation.toLocaleString()}`, 14, yPosition);
    yPosition += 7;
    doc.text(`Asset Utilization: ${utilization}%`, 14, yPosition);
    yPosition += 7;
    doc.text(`Current Asset Value: ₹${currentValue.toLocaleString()}`, 14, yPosition);
    yPosition += 15;

    // Depreciation Report
    doc.setFontSize(14);
    doc.text('Depreciation Report (Top 10)', 14, yPosition);
    yPosition += 10;
    
    doc.setFontSize(8);
    doc.text('Asset Code', 14, yPosition);
    doc.text('Asset Name', 50, yPosition);
    doc.text('Purchase', 110, yPosition);
    doc.text('Current', 140, yPosition);
    doc.text('Depreciation', 170, yPosition);
    yPosition += 5;
    
    reportData.depreciation.slice(0, 10).forEach(item => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(item.assetCode || '', 14, yPosition);
      doc.text((item.assetName || '').substring(0, 20), 50, yPosition);
      doc.text(`₹${item.purchaseValue.toLocaleString()}`, 110, yPosition);
      doc.text(`₹${item.currentValue.toLocaleString()}`, 140, yPosition);
      doc.text(`₹${item.depreciation.toLocaleString()}`, 170, yPosition);
      yPosition += 7;
    });

    yPosition += 10;

    // Utilization Report
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(14);
    doc.text('Utilization Report', 14, yPosition);
    yPosition += 10;
    
    doc.setFontSize(8);
    doc.text('Asset Type', 14, yPosition);
    doc.text('Total', 70, yPosition);
    doc.text('Assigned', 100, yPosition);
    doc.text('Utilization %', 140, yPosition);
    yPosition += 5;
    
    reportData.utilization.forEach(item => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(item.type, 14, yPosition);
      doc.text(item.total.toString(), 70, yPosition);
      doc.text(item.assigned.toString(), 100, yPosition);
      doc.text(`${item.utilizationRate}%`, 140, yPosition);
      yPosition += 7;
    });

    yPosition += 10;

    // Cost Analysis
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(14);
    doc.text('Cost Analysis', 14, yPosition);
    yPosition += 10;
    
    doc.setFontSize(8);
    doc.text('Type', 14, yPosition);
    doc.text('Count', 60, yPosition);
    doc.text('Total Purchase', 90, yPosition);
    doc.text('Total Current', 130, yPosition);
    doc.text('Depreciation', 170, yPosition);
    yPosition += 5;
    
    reportData.costAnalysis.forEach(item => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(item.type, 14, yPosition);
      doc.text(item.count.toString(), 60, yPosition);
      doc.text(`₹${item.totalPurchaseValue.toLocaleString()}`, 90, yPosition);
      doc.text(`₹${item.totalCurrentValue.toLocaleString()}`, 130, yPosition);
      doc.text(`₹${item.totalDepreciation.toLocaleString()}`, 170, yPosition);
      yPosition += 7;
    });

    doc.save(`Asset_Report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/userdashboard/staff/hrm/assets')}>
              <BackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight={600}>Asset Reports</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
            >
              Export Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<PdfIcon />}
              onClick={exportToPDF}
            >
              Export PDF
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Asset Type"
                value={filters.assetType}
                onChange={(e) => setFilters({ ...filters, assetType: e.target.value })}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="LAPTOP">Laptop</MenuItem>
                <MenuItem value="MOBILE">Mobile</MenuItem>
                <MenuItem value="DESKTOP">Desktop</MenuItem>
                <MenuItem value="VEHICLE">Vehicle</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="ASSIGNED">Assigned</MenuItem>
                <MenuItem value="RETURNED">Returned</MenuItem>
                <MenuItem value="DAMAGED">Damaged</MenuItem>
                <MenuItem value="LOST">Lost</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <DepreciationIcon sx={{ fontSize: 40, color: '#FF8042' }} />
                  <Box>
                    <Typography variant="h4" fontWeight={600}>
                      ₹{reportData.depreciation.reduce((sum, item) => sum + item.depreciation, 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Depreciation
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <UtilizationIcon sx={{ fontSize: 40, color: '#00C49F' }} />
                  <Box>
                    <Typography variant="h4" fontWeight={600}>
                      {Math.round((assets.filter(a => a.status === 'ASSIGNED').length / assets.length) * 100) || 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Asset Utilization
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CostIcon sx={{ fontSize: 40, color: '#0088FE' }} />
                  <Box>
                    <Typography variant="h4" fontWeight={600}>
                      ₹{assets.reduce((sum, asset) => sum + (asset.currentValue || 0), 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Current Asset Value
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label="Depreciation Report" />
            <Tab label="Utilization Report" />
            <Tab label="Cost Analysis" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* Depreciation Report */}
            {activeTab === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>Asset Depreciation Analysis</Typography>
                
                {/* Chart */}
                <Box sx={{ height: 300, mb: 3 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.depreciation.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="assetCode" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="purchaseValue" fill="#0088FE" name="Purchase Value" />
                      <Bar dataKey="currentValue" fill="#00C49F" name="Current Value" />
                      <Bar dataKey="depreciation" fill="#FF8042" name="Depreciation" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Table */}
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Asset Code</TableCell>
                        <TableCell>Asset Name</TableCell>
                        <TableCell align="right">Purchase Value</TableCell>
                        <TableCell align="right">Current Value</TableCell>
                        <TableCell align="right">Depreciation</TableCell>
                        <TableCell align="right">Rate %</TableCell>
                        <TableCell align="right">Age (Years)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.depreciation.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.assetCode}</TableCell>
                          <TableCell>{item.assetName}</TableCell>
                          <TableCell align="right">₹{item.purchaseValue.toLocaleString()}</TableCell>
                          <TableCell align="right">₹{item.currentValue.toLocaleString()}</TableCell>
                          <TableCell align="right">₹{item.depreciation.toLocaleString()}</TableCell>
                          <TableCell align="right">{item.depreciationRate}%</TableCell>
                          <TableCell align="right">{item.age}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Utilization Report */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>Asset Utilization by Type</Typography>
                
                <Grid container spacing={3}>
                  {/* Pie Chart */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={reportData.utilization}
                            dataKey="total"
                            nameKey="type"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label
                          >
                            {reportData.utilization.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>

                  {/* Table */}
                  <Grid item xs={12} md={6}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="right">Assigned</TableCell>
                            <TableCell align="right">Utilization</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.utilization.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>{item.type}</TableCell>
                              <TableCell align="right">{item.total}</TableCell>
                              <TableCell align="right">{item.assigned}</TableCell>
                              <TableCell align="right">
                                <Chip 
                                  label={`${item.utilizationRate}%`} 
                                  color={item.utilizationRate > 80 ? 'success' : item.utilizationRate > 50 ? 'warning' : 'error'}
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Cost Analysis */}
            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>Cost Analysis by Asset Type</Typography>
                
                {/* Chart */}
                <Box sx={{ height: 300, mb: 3 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.costAnalysis}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="type" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="totalPurchaseValue" fill="#0088FE" name="Total Purchase Value" />
                      <Bar dataKey="totalCurrentValue" fill="#00C49F" name="Total Current Value" />
                      <Bar dataKey="totalDepreciation" fill="#FF8042" name="Total Depreciation" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Table */}
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Asset Type</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Total Purchase Value</TableCell>
                        <TableCell align="right">Total Current Value</TableCell>
                        <TableCell align="right">Total Depreciation</TableCell>
                        <TableCell align="right">Avg Purchase Value</TableCell>
                        <TableCell align="right">Avg Current Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.costAnalysis.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.type}</TableCell>
                          <TableCell align="right">{item.count}</TableCell>
                          <TableCell align="right">₹{item.totalPurchaseValue.toLocaleString()}</TableCell>
                          <TableCell align="right">₹{item.totalCurrentValue.toLocaleString()}</TableCell>
                          <TableCell align="right">₹{item.totalDepreciation.toLocaleString()}</TableCell>
                          <TableCell align="right">₹{item.avgPurchaseValue.toLocaleString()}</TableCell>
                          <TableCell align="right">₹{item.avgCurrentValue.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AssetReports;
